import { useState } from 'react';
import { Slider } from '@sorrem-client/ui/components';
import styles from './SettingsPopover.module.scss';

interface VolumeRowProps {
    readonly id: string;
    readonly label: string;
    /** The saved volume, 0..1. */
    readonly volume: number;
    /** Heard at once while the slider moves, without saving. */
    readonly onPreview: (volume: number) => void;
    /** Saves the volume, once the slider is let go. */
    readonly onCommit: (volume: number) => void;
}

/**
 * One volume slider (#260). Dragging previews the volume live; letting go
 * (or a key press settling) saves it, so a drag is one account update, not
 * one per step.
 */
export function VolumeRow({ id, label, volume, onPreview, onCommit }: VolumeRowProps): JSX.Element {
    const [draft, setDraft] = useState<number | null>(null);
    const percent = Math.round((draft ?? volume) * 100);
    const commit = (): void => {
        if (draft !== null) {
            onCommit(draft);
            setDraft(null);
        }
    };

    return (
        <div className={styles.volumeRow}>
            <label className={styles.volumeLabel} htmlFor={id}>{label}</label>
            <Slider
                id={id}
                min={0}
                max={100}
                step={1}
                value={percent}
                aria-valuetext={`${percent}%`}
                data-ui-sound="none"
                onChange={(event) => {
                    const next = Number(event.target.value) / 100;
                    setDraft(next);
                    onPreview(next);
                }}
                onPointerUp={commit}
                onKeyUp={commit}
                onBlur={commit}
            />
            <span className={styles.volumeValue}>{percent}%</span>
        </div>
    );
}
